
import { useEffect, useRef, useCallback } from "react";

type ChatSocketOptions = {
  token: string | null | undefined;
  onNotification?: (msg: any) => void;
  onChatMessage?: (msg: any) => void;
  onUnreadCount?: (count: number) => void;
  onConnectionChange?: (connected: boolean) => void;
};

// Global singleton to track connection
let globalConnectionId = 0;

export function useChatSocket({
  token,
  onNotification,
  onChatMessage,
  onUnreadCount,
  onConnectionChange,
}: ChatSocketOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isConnectingRef = useRef(false);
  const connectionIdRef = useRef<number>(0);
  const shouldCleanupRef = useRef(false);
  const mountedRef = useRef(false);

  // Update refs when callbacks change
  const callbacksRef = useRef({
    onNotification,
    onChatMessage,
    onUnreadCount,
    onConnectionChange,
  });

  useEffect(() => {
    callbacksRef.current = {
      onNotification,
      onChatMessage,
      onUnreadCount,
      onConnectionChange,
    };
  }, [onNotification, onChatMessage, onUnreadCount, onConnectionChange]);

  const connectWebSocket = useCallback(() => {
    // Don't connect without token
    if (!token) {
      console.warn("[useChatSocket] ❌ No token provided");
      return;
    }

    // Already connecting or connected? Skip
    if (isConnectingRef.current) {
      console.log("[useChatSocket] ⚠️ Already connecting, skipping");
      return;
    }

    // Already have a valid connection? Keep it
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log("[useChatSocket] ✅ Already connected, keeping");
      return;
    }

    // Close existing connection if different
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      console.log("[useChatSocket] 🔌 Closing previous connection");
      shouldCleanupRef.current = true;
      wsRef.current.close();
      wsRef.current = null;
    }

    isConnectingRef.current = true;
    connectionIdRef.current = ++globalConnectionId;

    console.log(`[useChatSocket] 🔗 Connection #${connectionIdRef.current} starting`);
    console.log(`[useChatSocket] 📝 Component mounted: ${mountedRef.current}`);

    // SIMPLE URL - matching jQuery exactly
    const finalUrl = `ws://localhost:5002/api/v1/ws/chat?token=${encodeURIComponent(token)}`;
    console.log(`[useChatSocket] 🌐 URL: ${finalUrl}`);

    try {
      const ws = new WebSocket(finalUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current || shouldCleanupRef.current) {
          console.log("[useChatSocket] ⚠️ Component unmounted during connection, closing");
          ws.close();
          return;
        }
        
        console.log(`[useChatSocket] ✅ CONNECTED #${connectionIdRef.current}`);
        console.log(`[useChatSocket] 📊 ReadyState: ${ws.readyState}`);
        
        isConnectingRef.current = false;
        callbacksRef.current.onConnectionChange?.(true);
      };

      ws.onmessage = (event: MessageEvent) => {
        if (!mountedRef.current) {
          console.log("[useChatSocket] ⚠️ Received message but component unmounted");
          return;
        }
        
        try {
          const data = JSON.parse(event.data);
          console.log(`[useChatSocket] 📥 Message #${connectionIdRef.current}:`, {
            type: data.type || data.event || "unknown",
            keys: Object.keys(data)
          });

          const eventName = data.type || data.event;
          
          // Dispatch events
          switch (eventName) {
            case "notification":
              callbacksRef.current.onNotification?.(data);
              window.dispatchEvent(new CustomEvent("realesy:notification", { detail: data }));
              break;
            case "chat_message":
              callbacksRef.current.onChatMessage?.(data);
              window.dispatchEvent(new CustomEvent("realesy:chat_message", { detail: data }));
              break;
            case "message":
              callbacksRef.current.onChatMessage?.(data);
              window.dispatchEvent(new CustomEvent("realesy:message", { detail: data }));
              break;
            case "unread_count":
              const count = data.count || data.unread_count || 0;
              callbacksRef.current.onUnreadCount?.(count);
              window.dispatchEvent(new CustomEvent("realesy:unread_count", { detail: data }));
              break;
            default:
              window.dispatchEvent(new CustomEvent("realesy:websocket_message", { detail: data }));
          }
        } catch (error) {
          console.error("[useChatSocket] ❌ Parse error:", error);
        }
      };

      ws.onerror = (error) => {
        console.error(`[useChatSocket] ❌ Error #${connectionIdRef.current}:`, error);
        isConnectingRef.current = false;
        callbacksRef.current.onConnectionChange?.(false);
      };

      ws.onclose = (event) => {
        console.log(`[useChatSocket] 🔌 Closed #${connectionIdRef.current}:`, {
          code: event.code,
          wasClean: event.wasClean,
          mounted: mountedRef.current
        });
        
        isConnectingRef.current = false;
        
        // Only try to reconnect if component is still mounted and not intentionally cleaned up
        // if (mountedRef.current && !shouldCleanupRef.current && token) {
        //   console.log(`[useChatSocket] 🔄 Will reconnect in 3s`);
        //   reconnectTimerRef.current = setTimeout(() => {
        //     console.log("[useChatSocket] 🔄 Attempting reconnect...");
        //     connectWebSocket();
        //   }, 3000);
        // }
      };

    } catch (error) {
      console.error("[useChatSocket] ❌ Failed to create WebSocket:", error);
      isConnectingRef.current = false;
    }
  }, [token]);

  // Main effect - runs ONCE when component mounts
  useEffect(() => {
    console.log("[useChatSocket] 🚀 Component MOUNTING");
    mountedRef.current = true;
    shouldCleanupRef.current = false;
    
    // Connect WebSocket
    connectWebSocket();
    
    // Cleanup - runs when component unmounts
    return () => {
      console.log("[useChatSocket] 🧹 Component UNMOUNTING");
      mountedRef.current = false;
      shouldCleanupRef.current = true;
      
      // Clear reconnect timer
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      
      // Close WebSocket if still open
      if (wsRef.current) {
        console.log("[useChatSocket] 🔌 Closing WebSocket on unmount");
        // Don't set event handlers to null - let them fire but ignore
        wsRef.current.close();
        wsRef.current = null;
      }
      
      console.log("[useChatSocket] 🧹 Cleanup complete");
    };
  }, [connectWebSocket]); // Only depend on connectWebSocket

  return {
    wsRef,
    isConnected: wsRef.current?.readyState === WebSocket.OPEN
  };
}